import { Component, inject, OnInit, OnDestroy, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ConnectionService, ConnectionState } from 'ng-connection-service';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { SnackService } from '../../snack/snack.service';
import { CustomConnectionService } from '../../../service/custom-connection.service';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-wifi-connection',
  templateUrl: './wifi-connection.component.html',
  styleUrls: ['./wifi-connection.component.scss']
})
export class WifiConnectionComponent implements OnInit, OnDestroy {
  isConnected: boolean = navigator.onLine; // Initialize with browser's online status
  isManualOfflineMode = false;
  status!: string;
  currentState!: ConnectionState;
  subscription = new Subscription();

  // Use both services for compatibility but prefer CustomConnectionService
  connectionService = inject(ConnectionService);
  customConnectionService = inject(CustomConnectionService);
  snack = inject(SnackService);
  private dialog = inject(MatDialog);
  private cdr = inject(ChangeDetectorRef);

  @Output() wifiClicked = new EventEmitter<void>();
  // constructor() { }


  ngOnInit(): void {
    // Subscribe to the enhanced custom connection service
    this.subscription.add(
      this.customConnectionService.monitor().pipe(
        tap((newState: ConnectionState) => {
          this.currentState = newState;
          this.isManualOfflineMode = this.customConnectionService.getManualOfflineMode();
          const isOnline = newState.hasNetworkConnection && newState.hasInternetAccess;

          if (isOnline !== this.isConnected) {
            this.isConnected = isOnline;
            this.status = isOnline ? 'ONLINE' : 'OFFLINE';

            // Force change detection to ensure the UI updates immediately
            this.cdr.detectChanges();

            // Only show offline notifications (not online to avoid spam)
            if (!isOnline) {
              this.snack.showSnack('COMMON.PleaseInternet', 'error');
            }
          }
        })
      ).subscribe()
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  toggleConnectivityMode(): void {
    if (!this.isManualOfflineMode && !this.isConnected) {
      this.customConnectionService.refreshConnectivity();
      return;
    }

    const targetOfflineMode = !this.isManualOfflineMode;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        header: targetOfflineMode
          ? 'COMMON.CONNECTIVITY_CONFIRM_OFFLINE_HEADER'
          : 'COMMON.CONNECTIVITY_CONFIRM_ONLINE_HEADER',
        body: targetOfflineMode
          ? 'COMMON.CONNECTIVITY_CONFIRM_OFFLINE_BODY'
          : 'COMMON.CONNECTIVITY_CONFIRM_ONLINE_BODY',
        yes: 'COMMON.CONFIRM',
        no: 'COMMON.CANCEL'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.customConnectionService.setManualOfflineMode(targetOfflineMode);
      this.isManualOfflineMode = this.customConnectionService.getManualOfflineMode();
    });
  }

  triggerRefresh(): void {
    this.toggleConnectivityMode();
  }

  get tooltipKey(): string {
    if (this.isManualOfflineMode) {
      return 'COMMON.CONNECTIVITY_SWITCH_ONLINE';
    }

    if (!this.isConnected) {
      return 'COMMON.CONNECTIVITY_OFFLINE_CLICK_REFRESH';
    }

    return this.isConnected
      ? 'COMMON.CONNECTIVITY_SWITCH_OFFLINE'
      : 'COMMON.CONNECTIVITY_OFFLINE_CLICK_REFRESH';
  }
}
